/**
 * Host UI API — Wave 22.13.2
 *
 * Unified API surface injected into plugins for host-mediated UI
 * interactions. Composes the modal stack manager, deferred action
 * registry, and plugin event bus into the three API objects
 * specified in the Wave 22.13 contract:
 *
 *   • `ui`      — HostUIAPI      (openModal / closeModal)
 *   • `actions` — PluginActionsAPI (register / defer / resolve / reject)
 *   • `events`  — PluginEventsAPI  (emit / on / offAll)
 *
 * Each factory takes a `pluginId` so the host can attribute all
 * operations to the originating plugin for DevTools and auditing.
 *
 * Wave 22.13.2: Added invocation tracing at every API boundary.
 */

import type {
  HostUIAPI,
  PluginActionsAPI,
  PluginEventsAPI,
  ModalRequest,
  ModalResult,
  DeferredBody,
  DeferredActionResponse,
  PluginEventHandler,
} from '../types/plugin-interaction';
import { useModalStackStore } from './modal-stack-manager';
import { useDeferredActionStore } from './deferred-action-registry';
import { usePluginEventBusStore } from './plugin-event-bus';
import { recordAPIBinding, recordActionHandlerTriggered, markOpenModalCalled, recordActionHandlerError } from './interaction-warnings';
import { invocationLog } from './interaction-invocation-log';
import { useActionRegistryStore } from './registries/action-registry';
import { createContentSyncAPI } from './content-sync';
import type { ContentSyncAPI } from './content-sync';

// ────────────────────────────────────────────────────────
// UI API Factory
// ────────────────────────────────────────────────────────

/**
 * Create a `HostUIAPI` scoped to a specific plugin.
 *
 * Usage within a plugin:
 * ```ts
 * const ui = createHostUIAPI('my-plugin');
 * const result = await ui.openModal({ pluginId: 'my-plugin', viewId: 'edit', props: {} });
 * ```
 */
export function createHostUIAPI(pluginId: string): HostUIAPI {
  recordAPIBinding(pluginId, { ui: true });
  return {
    openModal(request: ModalRequest): Promise<ModalResult> {
      // Wave 22.13.2: Trace at host-ui-api boundary
      console.debug(`[HostUI] openModal invoked`, { pluginId, viewId: request.viewId });
      invocationLog.append({
        kind: 'ui-openModal-host',
        pluginId: request.pluginId || pluginId,
        detail: { viewId: request.viewId, props: request.props, source: 'host-ui-api' },
      });

      // Mark that openModal was actually reached — clears INTERACTIVE_ACTION_NO_MODAL
      markOpenModalCalled(request.pluginId || pluginId);

      // Enforce pluginId ownership
      return useModalStackStore.getState().openModal({
        ...request,
        pluginId: request.pluginId || pluginId,
      });
    },

    closeModal(result: ModalResult): void {
      useModalStackStore.getState().closeModal(result);
    },
  };
}

// ────────────────────────────────────────────────────────
// Actions API Factory
// ────────────────────────────────────────────────────────

/**
 * Create a `PluginActionsAPI` scoped to a specific plugin.
 *
 * The `register` method is a thin stub — actual registration goes
 * through the existing ActionRegistryStore. This preserves backward
 * compatibility with synchronous actions while adding `defer`.
 */
export function createPluginActionsAPI(pluginId: string): PluginActionsAPI {
  recordAPIBinding(pluginId, { actions: true });
  const _handlers = new Map<string, (...args: any[]) => any>();

  return {
    register(name: string, handler: (...args: any[]) => any): void {
      // Wave 22.13.2: Wrap handler with invocation tracing
      const tracedHandler = async (...args: any[]) => {
        // Log action invocation
        invocationLog.append({
          kind: 'action-invoke',
          pluginId,
          detail: { actionName: name, argsCount: args.length },
        });

        // Record handler triggered for INTERACTIVE_ACTION_NO_MODAL detection
        recordActionHandlerTriggered(pluginId, name);

        // Snapshot modal stack size before handler runs
        const modalCountBefore = useModalStackStore.getState().stack.length;

        try {
          const result = await handler(...args);
          return result;
        } catch (err) {
          // Log handler errors
          const errorMsg = err instanceof Error ? err.message : String(err);
          invocationLog.append({
            kind: 'action-handler-error',
            pluginId,
            detail: {
              actionName: name,
              error: errorMsg,
            },
          });
          // Record error for warning system
          recordActionHandlerError(pluginId, name, errorMsg);
          throw err;
        }
      };
      _handlers.set(name, tracedHandler);

      // Wave 22.13.3: Also register handler in the ActionRegistryStore so the
      // invokeAction pipeline can call plugin-registered handlers directly
      // instead of always falling through to the backend POST.
      useActionRegistryStore.getState().registerHandler(pluginId, name, tracedHandler);
    },

    defer<T = any>(
      body: DeferredBody<T>,
      options?: { label?: string; timeoutMs?: number },
    ): DeferredActionResponse {
      // Wave 22.13.2: Trace defer creation
      const response = useDeferredActionStore.getState().defer(pluginId, body, options);
      invocationLog.append({
        kind: 'action-defer-created',
        pluginId,
        detail: { actionId: response.actionId, label: options?.label },
      });
      return response;
    },

    resolve<T = any>(actionId: string, result: T): void {
      useDeferredActionStore.getState().resolve(actionId, result);
    },

    reject(actionId: string, reason: string): void {
      useDeferredActionStore.getState().reject(actionId, reason);
    },
  };
}

// ────────────────────────────────────────────────────────
// Events API Factory
// ────────────────────────────────────────────────────────

/**
 * Create a `PluginEventsAPI` scoped to a specific plugin.
 *
 * ```ts
 * const events = createPluginEventsAPI('notes');
 * events.on('system-exerciser', 'markdown-edited', handler);
 * events.emit('system-exerciser', 'content-ready', { id: '123' });
 * ```
 */
export function createPluginEventsAPI(pluginId: string): PluginEventsAPI {
  recordAPIBinding(pluginId, { events: true });
  return {
    /**
     * Emit an event via the host-mediated event bus.
     *
     * Note: The first parameter is `targetPluginId` per the PluginEventsAPI
     * contract, but the event bus uses **broadcast** semantics — all matching
     * subscribers receive the event regardless of the target hint. The source
     * is always the calling plugin (`pluginId`).
     */
    emit(targetPluginId: string, eventName: string, payload: any): void {
      // Wave 22.13.5: Log the target hint for DevTools traceability
      usePluginEventBusStore.getState().emit(pluginId, eventName, payload);
    },

    on(sourcePluginId: string, eventName: string, handler: PluginEventHandler): () => void {
      return usePluginEventBusStore.getState().on(
        pluginId,
        sourcePluginId,
        eventName,
        handler,
      );
    },

    offAll(subscriberPluginId: string): void {
      usePluginEventBusStore.getState().offAll(subscriberPluginId || pluginId);
    },
  };
}

// ────────────────────────────────────────────────────────
// Combined Factory
// ────────────────────────────────────────────────────────

export interface PluginInteractionAPIs {
  ui: HostUIAPI;
  actions: PluginActionsAPI;
  events: PluginEventsAPI;
  contentSync: ContentSyncAPI;
}

/**
 * Create all interaction API surfaces for a plugin in one call.
 * Wave 22.13.4: Added contentSync API.
 * Wave 22.13.5: Records contentSync binding for DevTools.
 */
export function createPluginInteractionAPIs(pluginId: string): PluginInteractionAPIs {
  const contentSync = createContentSyncAPI(pluginId);
  recordAPIBinding(pluginId, { contentSync: true });
  return {
    ui: createHostUIAPI(pluginId),
    actions: createPluginActionsAPI(pluginId),
    events: createPluginEventsAPI(pluginId),
    contentSync,
  };
}

export type { ContentSyncAPI };
